import Alert from '@storeblocks/alert';
import LoadingIndicator from '@storeblocks/loading-indicator';
import React from 'react';
import { useParams } from 'react-router';

import { useFlag } from '@unleash/proxy-client-react';
import { useInsuranceDetailsWithPotentialChanges } from 'Api/insurance';
import { ProductName } from 'Constants/products.constants';
import { AutomaticCancellationForm } from 'Forms/CancelForm';
import { EmailCancellation } from 'Forms/CancelForm/EmailCancellation';
import { CancelAkaHouseContentsForm } from 'Forms/CancelFormV2/products/akaHouseContents/CancelAkaHouseContentsForm';
import { CancelAkaStudentForm } from 'Forms/CancelFormV2/products/akaStudent/CancelAkaStudentForm';
import { CancelAkaTravelForm } from 'Forms/CancelFormV2/products/akaTravel/CancelAkaTravelForm';
import { CancelElectricScooterForm } from 'Forms/CancelFormV2/products/electricScooter/CancelElectricScooterForm';
import { CancelTravelForm } from 'Forms/CancelFormV2/products/travel/CancelTravelForm';
import { isDeregisterableVehicleInsurance } from 'Utils/insuranceTypes/insuranceTypes.utils';
import { getAppText } from 'Utils/text';
import {
  isAutomaticallyCancellableInsurance,
  isAutomaticallyCancellableVehicleInsurance,
  showAkaHouseContentsCwpCancellationForm,
  showAkaStudentCwpCancellationForm,
  showAkaTravelCwpCancellationForm,
  showElectricScooterCwpCancellationForm,
  showTravelCwpCancellationForm,
} from './cancelInsurancePage.helpers';
import { AlertContainer } from './cancelInsurancePage.styled';

export const CancelInsuranceSection = (): React.ReactElement | null => {
  const { policyNumber = '' } = useParams<{ policyNumber: string }>();
  const electricScooterCancellationToggle = useFlag(
    'cancel-ELECTRIC_SCOOTER_INSURANCE',
  );
  const travelCancellationToggle = useFlag('cancel-TRAVEL_INSURANCE');
  const akaHouseContentsToggle = useFlag('cancel-AKA_HOUSE_CONTENTS_INSURANCE');
  const akaTravelToggle = useFlag('cancel-AKA_TRAVEL_INSURANCE');
  const akaStudentToggle = useFlag('cancel-AKA_STUDENT_INSURANCE');

  const {
    insuranceDetailsWithPotentialChanges: insuranceDetails,
    loading,
    error,
  } = useInsuranceDetailsWithPotentialChanges(policyNumber);

  if (error) {
    return (
      <Alert
        variant="error"
        title={getAppText('misc.error.fetchInsuranceDetailed.title')}
        description={getAppText(
          'misc.error.fetchInsuranceDetailed.description',
        )}
      />
    );
  }

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!insuranceDetails) {
    return (
      <Alert
        variant="error"
        title={getAppText('cancel.alert.cantFindPolicy.title')}
        description={getAppText('cancel.alert.cantFindPolicy.description')}
      />
    );
  }

  const showCwpCancellationForm =
    showElectricScooterCwpCancellationForm(
      insuranceDetails,
      electricScooterCancellationToggle,
    ) ||
    showTravelCwpCancellationForm(insuranceDetails, travelCancellationToggle) ||
    showAkaHouseContentsCwpCancellationForm(
      insuranceDetails,
      akaHouseContentsToggle,
    ) ||
    showAkaTravelCwpCancellationForm(insuranceDetails, akaTravelToggle) ||
    showAkaStudentCwpCancellationForm(insuranceDetails, akaStudentToggle);

  if (showCwpCancellationForm) {
    switch (insuranceDetails.insuranceProduct) {
      case ProductName.ELECTRIC_SCOOTER:
        return <CancelElectricScooterForm insurance={insuranceDetails} />;
      case ProductName.TRAVEL:
        return <CancelTravelForm insurance={insuranceDetails} />;
      case ProductName.AKA_HOUSE_CONTENTS:
        return <CancelAkaHouseContentsForm insurance={insuranceDetails} />;
      case ProductName.AKA_TRAVEL:
        return <CancelAkaTravelForm insurance={insuranceDetails} />;
      case ProductName.AKA_STUDENT:
        return <CancelAkaStudentForm insurance={insuranceDetails} />;
      default:
        return null;
    }
  }

  const automaticallyCancellableInsurance =
    isAutomaticallyCancellableInsurance(insuranceDetails);

  if (
    automaticallyCancellableInsurance ||
    isAutomaticallyCancellableVehicleInsurance(insuranceDetails)
  ) {
    return <AutomaticCancellationForm insuranceDetails={insuranceDetails} />;
  }

  if (
    !automaticallyCancellableInsurance &&
    !isDeregisterableVehicleInsurance(insuranceDetails.insuranceProduct)
  ) {
    return <EmailCancellation insuranceDetails={insuranceDetails} />;
  }

  return (
    <AlertContainer>
      <Alert
        variant="error"
        title={getAppText('cancelInsurance.error.notCancellable')}
      />
    </AlertContainer>
  );
};
